<template>
	<b-card>
		<div class="mb-0 d-flex flex-row align-items-center text-primary">
			<b-card-title title-tag="h3" class="flex-grow-1">{{region.title}}</b-card-title>

			<b-button
				@click="removeRegion()"
				variant="outline-danger"
				class="border-0 mr-1 mr-sm-2 mr-md-4"
				title="remove region"
			>
				<span class="d-none d-sm-none d-md-inline-block text-white">delete&nbsp;</span>
				<font-awesome-icon :icon="['fas', 'trash-alt']" small fixed-width />
			</b-button>
			<font-awesome-icon
				icon="align-justify"
				class="ddMoveRIcon"
				title="move region position"
				fixed-width
			/>
		</div>
		<!-- region move icon -->

		<b-card-body class="mt-2 hide-by-moving">
			<!-- region basic info -->
			<b-form-row>
				<!-- region title -->
				<b-col cols="12" sm="8" md="3" lg="3">
					<b-form-group :label-for="'regionTitle' + region.id" description="region title">
						<b-form-input
							:id="'regionTitle' + region.id"
							v-model.trim="region.title"
							type="text"
							:name="'regionTitle' + region.id"
						/>
					</b-form-group>
				</b-col>
			</b-form-row>
			<b-form-row>
				<!-- region description -->
				<b-col cols="12" sm="12" md="10" lg="10">
					<b-form-group :label-for="'regionDesription' + region.id" description="region description">
						<b-form-input
							:id="'regionDesription' + region.id"
							v-model.trim="region.description"
							type="text"
							:name="'regionDesription' + region.id"
						/>
					</b-form-group>
				</b-col>
			</b-form-row>

			<b-form-row>
				<b-col cols="12">
					<!-- loop questions -->
					<b-list-group>
						<!-- @end="ddOnEnd(region.id, ...arguments)" -->
						<draggable
							:list="questions"
							handle=".ddMoveQIcon"
							:group="'questions-under-region-' + region.id"
							@end="questionPositionMoved(region.id, ...arguments)"
						>
							<b-list-group-item
								:class="[question.pivot.enabled ? 'text-muted' : 'text-warning']"
								v-for=" question in questions"
								:key="'question-' + region.id + '-' + question.id"
							>
								<div class="d-flex flex-column flex-sm-column flex-md-row align-items-center">
									<!-- question -->
									<div :title="question.description" class="text-info text-shadow mr-auto">
										<span class="h-100">
											<!-- grab icon -->
											<font-awesome-icon
												title="drag & drop to move the question"
												icon="align-justify"
												class="ddMoveQIcon mr-2"
												fixed-width
											/>
										</span>
										<!-- question title -->
										<span class="flex-shrink-1">{{ question.title }}</span>
									</div>

									<!-- answers container  -->
									<div class="mt-4 mt-sm-3 mt-md-0 mt-lg-0 mt-xl-0 text-sm-left text-md-right" label>
										<!-- answers  -->
										<b-form-radio-group disabled inline>
											<b-form-radio
												v-for="answer in question.answers"
												:key="answer.id + question.id"
												:title="answer.description"
											>
												<span>{{ answer.title }}</span>
											</b-form-radio>
										</b-form-radio-group>
									</div>

									<!-- question action buttons  -->
									<!-- enable / disable  -->
									<div
										class="ml-auto ml-sm-auto ml-md-0 d-flex flex-row justify-content-end align-self-center mt-2 mt-md-0 mt-lg-0 mt-xl-0"
									>
										<b-form-checkbox
											v-model="question.pivot.enabled"
											:value="1"
											:unchecked-value="0"
											:key="'enabled-' + region.id + '-' + question.id"
											:name="'enabled-' + region.id + '-' + question.id"
											switch
											size="sm"
											:id="'enabled-' + region.id + '-' + question.id"
											style="margin-top:5px"
											title="disable or enable this question"
										></b-form-checkbox>

										<!-- remove question button -->
										<b-button
											@click="removeQuestion(question)"
											size="sm"
											variant="outline-danger"
											class="border-0"
											title="remove this question from template"
										>
											<font-awesome-icon :icon="['fas', 'trash-alt']" small fixed-width />
										</b-button>
									</div>
								</div>
							</b-list-group-item>
							<!--/ end for each -->

							<!--saving / loading indication -->
							<b-list-group-item v-if="$wait.is('saving question')">
								<div class="d-flex align-items-center h-100">
									<font-awesome-icon :icon="['fas', 'circle-notch']" spin />
									<small class="text-info pl-2">
										&nbsp;
										<em>saving question</em>
									</small>
								</div>
							</b-list-group-item>
							<!-- 
								
								add new question dialog 
								
							-->
							<b-list-group-item class="text-muted" :key="'new-question-' + region.id">
								<div
									class="d-flex flex-column flex-sm-column flex-md-row justify-content-between align-items-center"
								>
									<transition name="fade" mode="out-in">
										<!-- add question button -->
										<b-button
											variant="light"
											small
											block
											v-if="!creatingNewQUestion"
											@click.prevent="doCreateNewQuestion()"
											title="add new Question to the Template"
										>
											<font-awesome-icon icon="plus" small fixed-width />&nbsp;Add Question
										</b-button>
										<!-- 
											
											place holder for new question (drop down with questions)
											
										-->
										<newQuestion
											v-else
											:language="language"
											:questions="availableQuestionsPerRegion"
											@questionAdded="addNewQestionToRegion"
										></newQuestion>
									</transition>
								</div>
							</b-list-group-item>
						</draggable>
					</b-list-group>
				</b-col>
			</b-form-row>
		</b-card-body>
	</b-card>
</template>

<script>
	import { mapState, mapActions } from "vuex";
	import draggable from "vuedraggable";
	import newQuestion from "@/components/question/questionAddNew.vue";
	import { differenceBy, map } from "lodash"; // eslint-disable-line no-unused-vars

	export default {
		name: "RegionEdit_component",

		// components
		components: { draggable, newQuestion },

		// props
		props: {
			region: Object,
			questions: {
				type: Array,
				default: function () {
					return [];
				},
			},
			language: Object,
		},

		// data
		data() {
			return {
				// is the new question creating atm
				creatingNewQUestion: false,
			};
		},

		computed: {
			// get apt
			...mapState("question", { availableQuestions: "items" }),

			isEnabled(question) {
				return question.enabled;
			},

			// filter the questions that are not already in that region
			availableQuestionsPerRegion() {
				return differenceBy(this.availableQuestions, this.questions, "id");
			},
		},

		methods: {
			// map vuex action to update the apt
			...mapActions("apTemplate", [
				"INSERT_QUESTION_TO_TEMPLATE",
				"DELETE_QUESTION_FROM_TEMPLATE",
			]),
			...mapActions("question", ["ADD_ITEM"]),

			// the QUESTION changed the position, update item.position = index
			questionPositionMoved() {
				this.questions.map(function (item, index) {
					item.pivot.position = index + 1;
				});

				this.$emit("questionsOrderChanged", this.region.id, this.questions);
			},

			// create nw question, show new question interface
			// go grab all available question from db
			doCreateNewQuestion() {
				// show loader
				this.$wait.start("loading questions");

				this.creatingNewQUestion = true;

				// fetch all available questions (stored in db)
				this.$store
					.dispatch("question/GET_QUESTION_LIST", {
						language_id: this.language.id,
					})
					.then(
						() => {
							this.$wait.end("loading questions");
						},
						(error) => {
							this.$wait.end("loading questions");
							this.showToast("danger", error.statusText, error.data.message);
						}
					);
			},

			// add new button component $event-it that the new question has been added to the region
			// pass it to parent
			addNewQestionToRegion(question) {
				// create data for update pivot table
				const pivotData = {
					APTemplate_id: this.region.APTemplate_id,
					APQuestion_id: question.id,
					region_id: this.region.id,
					position: this.questions.length + 1 || 1,
					enabled: 1,
				};

				// get the form data and submit it to backend
				this.INSERT_QUESTION_TO_TEMPLATE({
					id: this.region.APTemplate_id,
					data: pivotData,
				}).then(
					// true
					(ok) => {
						// add pivot (region-question) infos to question
						question.pivot = pivotData;

						// add this question to other available questions
						this.ADD_ITEM(question);
						// this.answersByFeels = this.groupAnswersByFeels;

						// update parent
						this.$emit("questionsAddedRemoved");

						this.$wait.end("saving question");
					},
					(error) => {
						console.log("error: ", error);
						this.$wait.end("saving question");
						this.showToast("danger", error.statusText, error.data.message);
					}
				);
			},

			// remove this question from template,
			// send it to backend to remove it from pivot table
			removeQuestion(question) {
				// let the user know that question will be removed, let him confirm the action
				this.$bvModal
					.msgBoxConfirm(
						"I hate to interrupt you, but I need to be sure that you know that this will remove this question from this Region. Are you sure you wanna do that?",
						{
							title: "Please Confirm",
							okVariant: "danger",
							okTitle: "Sure",
							cancelVariant: "light",
							cancelTitle: "Cancel",
							footerClass: "p-2",
						}
					)
					.then((value) => {
						if (value) {
							// create data for update pivot table
							const pivotData = {
								APTemplate_id: this.region.APTemplate_id,
								APQuestion_id: question.id,
								region_id: this.region.id,
							};

							// get the form data and submit it to backend
							this.DELETE_QUESTION_FROM_TEMPLATE({
								id: this.region.APTemplate_id,
								data: pivotData,
							}).then(
								// true
								() => {
									// update parent
									this.$emit("questionsAddedRemoved");
								},
								(error) => {
									console.log("error", error);
									this.showToast("danger", error.statusText, error.data.message);
								}
							);
						}
					})
					.catch((err) => {
						console.error("error in modal: ", err);
					});
			},

			removeRegion() {
				// let the parent delete it
				// let the user know that question will be removed, let him confirm the action
				this.$bvModal
					.msgBoxConfirm(
						"I hate to interrupt you, but I need to be sure that you know that this will remove this Region from this Template. Are you sure you wanna do that?",
						{
							title: "Please Confirm",
							okVariant: "danger",
							okTitle: "Sure",
							cancelVariant: "light",
							cancelTitle: "Cancel",
							footerClass: "p-2",
						}
					)
					.then((value) => {
						if (value) this.$emit("removeMe", this.region.id);
					})
					.catch((err) => {
						console.error("error in modal: ", err);
					});
			},
		},
	};
</script>


<style scoped>
	/* drag-drop move icon */
	.ddMoveRIcon,
	.ddMoveQIcon {
		cursor: move; /* fallback if grab cursor is unsupported */
		cursor: grab;
		cursor: -moz-grab;
		cursor: -webkit-grab;
	}
	/* region is moving */
	.ghost {
		color: orange;
	}
	/* hide regions body when on of them is moving */
	.region-is-moving .hide-by-moving {
		display: none;
		overflow: hidden;
		background-color: olivedrab;
	}
</style>
